import { inject, Injectable } from '@angular/core';
import { ServiceType } from '@common/enums';
import { Domain, DomainServicesState, DomainStatistic, UserServiceBackupInfo } from '@common/models';
import { AbilityService, AuthContextService, DomainService } from '@common/services';
import { DomainStateService } from '@common/services/domain/domain-state.service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DomainEffectService {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);
  private readonly domainService = inject(DomainService);
  private readonly domainState = inject(DomainStateService);

  protected readonly authDomainId = this.authContext.domainId;
  protected readonly isAdmin = this.ability.isAdmin;

  private readonly domain = this.domainState.domain;
  private readonly domainStatistic = this.domainState.domainStatistic;
  private readonly domainServicesState = this.domainState.domainServicesState;

  fetchDomain(forceUpdate = false): Observable<Domain> {
    if (this.domain() && !forceUpdate) return of(this.domain());

    if (!this.isAdmin()) {
      return of(null);
    }

    return this.domainService.fetchDomain(this.authDomainId()).pipe(tap((info) => this.domainState.patchField('domain', info)));
  }

  fetchDomainStatistic(forceUpdate = false): Observable<DomainStatistic> {
    if (this.domainStatistic() && !forceUpdate) return of(this.domainStatistic());

    return this.domainService.fetchDomainStatistic().pipe(tap((info) => this.domainState.patchField('domainStatistic', info)));
  }

  fetchDomainServicesState(forceUpdate = false): Observable<DomainServicesState> {
    if (this.domainServicesState() && !forceUpdate) return of(this.domainServicesState());

    return this.domainService.fetchDomainServicesState().pipe(tap((info) => this.domainState.patchField('domainServicesState', info)));
  }

  fetchDomainServiceInfo(
    service: ServiceType.TeamDrives | ServiceType.SharePoint | ServiceType.Teams | ServiceType.Contacts
  ): Observable<UserServiceBackupInfo> | Observable<null> {
    const serviceName = this.resolveServiceName(service);

    if (!serviceName) {
      return of(null);
    }

    return this.domainService.fetchDomainServiceInfo(serviceName, this.authDomainId());
  }

  private resolveServiceName(service: ServiceType): string {
    switch (service) {
      case ServiceType.TeamDrives:
        return 'TeamDrive';
      case ServiceType.SharePoint:
        return 'Site';
      case ServiceType.Teams:
        return 'Team';
      case ServiceType.Contacts:
        return 'Contact';
      default:
        return '';
    }
  }

  syncDomain(): Observable<void> {
    return this.domainService.syncDomain(this.domain()?.Id);
  }
}
